// All Chakra pieces come from web-design's bundled runtime — the bare
// @chakra-ui/react specifier resolves to an unthemed transitive copy.
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Skeleton,
  Spinner,
  Text,
} from '@elkaso-app/web-design';
import { useInfiniteSalesLookupFamiliesApi } from 'apis/sales-lookup';
import { useDebounce } from 'hooks/use-debounce';
import { usePageParams } from 'hooks/use-page-params';
import { FamilyCard } from 'pages/sales-lookup/components/family-card';
import { MobileShell } from 'pages/sales-lookup/components/mobile-shell';
import { resolveVatMode } from 'pages/sales-lookup/utils/vat';
import qs from 'query-string';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

export const LookupListScreen = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();
  const vatMode = resolveVatMode(params.vat);

  useEffect(() => {
    dispatch(changeTitle('Sales Lookup'));
  }, []);

  const [searchInput, setSearchInput] = useState((params.search as string) || '');
  const debouncedSearch = useDebounce(searchInput, 300);

  // Debounced input → `search` URL param (the hook reads the URL).
  useEffect(() => {
    const next = getPageParams();
    if (((next.search as string | undefined) ?? '') === debouncedSearch) return;
    setPageParams({ ...next, search: debouncedSearch || undefined });
  }, [debouncedSearch]);

  const { data, isLoading, isPreviousData, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteSalesLookupFamiliesApi();
  const families = useMemo(() => (data?.pages ?? []).flatMap((page) => page.data), [data]);

  // keepPreviousData holds the old rows on screen while a new search resolves;
  // isPreviousData is true exactly during that window — the search's loading
  // state (background refetches of the SAME search don't trigger it).
  const isSearching = isPreviousData;

  // Window-scrolled infinite list: fetch the next page as the sentinel nears
  // the viewport (same IntersectionObserver pattern as the Prices family pane).
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      // The shell owns scrolling (document scroll is globally disabled), so the
      // observer must use it as root — otherwise rootMargin's 300px prefetch
      // is measured against the viewport and never widens the container's clip.
      { root: sentinel.closest('[data-scroll-root]'), rootMargin: '300px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const openFamily = (familyId: number) => {
    const { restaurantId, restaurantName, vat, search } = getPageParams();
    history.push({
      pathname: `/app/sales-lookup/families/${familyId}`,
      search: qs.stringify({ restaurantId, restaurantName, vat, search }, { skipEmptyString: true }),
    });
  };

  return (
    <MobileShell>
      <Text fontSize='11px' fontWeight='semibold' letterSpacing='0.12em' color='gray.500' mb='10px'>
        QUOTE A PRICE
      </Text>

      <InputGroup mb='14px'>
        <InputLeftElement pointerEvents='none' h='full'>
          <FiSearch color='var(--chakra-colors-gray-400)' size='16px' />
        </InputLeftElement>
        <Input
          bg='white'
          borderRadius='12px'
          placeholder='Search products, brands, origin…'
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {isSearching && (
          <InputRightElement h='full'>
            <Spinner size='sm' color='red.500' />
          </InputRightElement>
        )}
      </InputGroup>

      {isLoading ? (
        <>
          <Skeleton h='74px' borderRadius='14px' mb='10px' />
          <Skeleton h='74px' borderRadius='14px' mb='10px' />
          <Skeleton h='74px' borderRadius='14px' mb='10px' />
        </>
      ) : (
        <Box opacity={isSearching ? 0.45 : 1} transition='opacity 0.15s ease'>
          {families.length ? (
            families.map((family) => (
              <FamilyCard
                key={family.familyId}
                family={family}
                vatMode={vatMode}
                onClick={() => openFamily(family.familyId)}
              />
            ))
          ) : (
            <Text fontSize='14px' color='gray.500' textAlign='center' py='40px'>
              No products found
            </Text>
          )}
        </Box>
      )}

      <Box ref={sentinelRef} h='1px' />
      {isFetchingNextPage && (
        <Flex justify='center' py='12px'>
          <Spinner size='sm' color='red.500' />
        </Flex>
      )}
    </MobileShell>
  );
};
